import { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router'
import { motion, AnimatePresence } from 'motion/react'
import { useAuth } from '@/lib/auth-context'
import { useGoogleAuth } from '@/hooks/useGoogleAuth'
import { RecostLogo } from '@/components/RecostLogo'
import { colors, accent } from '@/lib/tokens'

const ease = [0.16, 1, 0.3, 1] as const

const NAV_LINKS = [
  { label: 'Overview', href: '/overview' },
  { label: 'Documentation', href: '/docs' },
]

function GitHubIcon({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
    </svg>
  )
}

function NavLink({ href, label }: { href: string; label: string }) {
  const { pathname } = useLocation()
  const isExternal = href.startsWith('http')
  const isActive = !isExternal && href !== '/#features' && pathname.startsWith(href)

  const style: React.CSSProperties = {
    fontSize: 14,
    fontWeight: isActive ? 600 : 400,
    color: isActive ? accent : colors.textSecondary,
    textDecoration: 'none',
    padding: '0 14px',
    height: 36,
    borderRadius: 6,
    transition: 'color 0.15s',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    lineHeight: 1,
  }

  if (href.startsWith('/#')) {
    return (
      <a
        href={href}
        style={style}
        onMouseEnter={(e) => (e.currentTarget.style.color = colors.textPrimary)}
        onMouseLeave={(e) => (e.currentTarget.style.color = isActive ? accent : colors.textSecondary)}
      >
        {label}
      </a>
    )
  }

  return (
    <Link
      to={href}
      style={style}
      onMouseEnter={(e) => (e.currentTarget.style.color = colors.textPrimary)}
      onMouseLeave={(e) => (e.currentTarget.style.color = isActive ? accent : colors.textSecondary)}
    >
      {label}
    </Link>
  )
}

function MobileMenu({
  open,
  onClose,
  onLogin,
  isAuthenticated,
}: {
  open: boolean
  onClose: () => void
  onLogin: () => void
  isAuthenticated: boolean
}) {
  const navigate = useNavigate()

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={onClose}
            style={{ position: 'fixed', inset: 0, zIndex: 40, background: 'rgba(0,0,0,0.2)' }}
          />
          {/* Drawer */}
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.2, ease }}
            style={{
              position: 'fixed',
              top: 68,
              left: 12,
              right: 12,
              zIndex: 50,
              background: colors.bgBase,
              border: `1px solid ${colors.borderDefault}`,
              borderRadius: 12,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              padding: '16px 0',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {NAV_LINKS.map((link) => (
              <a
                key={link.label}
                href={link.href.startsWith('/') && !link.href.startsWith('/#') ? undefined : link.href}
                onClick={() => {
                  onClose()
                  if (link.href.startsWith('/') && !link.href.startsWith('/#')) {
                    navigate(link.href)
                  }
                }}
                style={{
                  
                  fontSize: 15,
                  fontWeight: 500,
                  color: colors.textPrimary,
                  textDecoration: 'none',
                  padding: '11px 20px',
                  transition: 'background 0.12s',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = colors.bgPage)}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                {link.label}
              </a>
            ))}
            <a
              href="https://github.com/recost-dev/"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: 15, fontWeight: 500,
                color: colors.textPrimary, textDecoration: 'none', padding: '11px 20px',
                display: 'flex', alignItems: 'center', gap: 8, transition: 'background 0.12s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = colors.bgPage)}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              onClick={onClose}
            >
              <GitHubIcon />
              GitHub
            </a>

            <div style={{ height: 1, background: colors.borderDefault, margin: '8px 0' }} />

            {isAuthenticated ? (
              <button
                onClick={() => { onClose(); navigate('/dashboard') }}
                style={{
                  fontSize: 15, fontWeight: 600,
                  color: colors.bgBase, margin: '4px 16px', padding: '12px 0', borderRadius: 8,
                  background: accent, cursor: 'pointer', transition: 'opacity 0.15s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.88')}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
              >
                Open dashboard
              </button>
            ) : (
              <>
                <button
                  onClick={() => { onClose(); onLogin() }}
                  style={{
                    fontSize: 15, fontWeight: 500,
                    color: colors.textSecondary, padding: '11px 20px', background: 'transparent',
                    cursor: 'pointer', textAlign: 'left', transition: 'background 0.12s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = colors.bgPage)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  Sign in
                </button>
                <button
                  onClick={() => { onClose(); onLogin() }}
                  style={{
                    fontSize: 15, fontWeight: 600,
                    color: colors.bgBase, margin: '4px 16px', padding: '12px 0', borderRadius: 8,
                    background: accent, cursor: 'pointer', transition: 'opacity 0.15s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.88')}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                >
                  Get started free
                </button>
              </>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export function Navigation() {
  const { isAuthenticated, login: applyToken } = useAuth()
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [authError, setAuthError] = useState<string | null>(null)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)
      if (!mobile) setMobileOpen(false)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const { login } = useGoogleAuth(
    (token) => {
      applyToken(token)
      navigate('/dashboard')
    },
    (msg) => setAuthError(msg),
  )

  return (
    <>
      <header
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          backgroundColor: 'rgba(250,250,249,0.95)',
          backdropFilter: 'blur(14px)',
          borderBottom: `1px solid ${colors.borderDefault}`,
          overflowX: 'hidden',
        }}
      >
        <div style={{ maxWidth: isMobile ? '100%' : '85%', margin: '0 auto', padding: isMobile ? '0 16px' : '0 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', height: 64 }}>
            {/* Logo */}
            <Link
              to="/"
              style={{
                textDecoration: 'none',
                flexShrink: 0,
                marginRight: isMobile ? 0 : 32,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <RecostLogo size={26} />
            </Link>

            {/* Spacer left — pushes nav to center */}
            {!isMobile && <div style={{ flex: 1 }} />}

            {/* Desktop nav */}
            <nav style={{ display: isMobile ? 'none' : 'flex', alignItems: 'center', gap: 8 }}>
              {NAV_LINKS.map((l) => (
                <NavLink key={l.label} href={l.href} label={l.label} />
              ))}
              <a
                href="https://github.com/recost-dev/"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: 14,
                  fontWeight: 400,
                  color: colors.textSecondary,
                  textDecoration: 'none',
                  padding: '0 14px',
                  height: 36,
                  borderRadius: 6,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  lineHeight: 1,
                  transition: 'color 0.15s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = colors.textPrimary)}
                onMouseLeave={(e) => (e.currentTarget.style.color = colors.textSecondary)}
              >
                <GitHubIcon />
                GitHub
              </a>
            </nav>

            {/* Spacer */}
            {!isMobile && <div style={{ flex: 1 }} />}

            {/* Desktop CTAs */}
            <div style={{ display: isMobile ? 'none' : 'flex', alignItems: 'center', gap: 8 }}>
              {isAuthenticated ? (
                <Link
                  to="/dashboard"
                  style={{
                    
                    fontSize: 14,
                    fontWeight: 600,
                    color: colors.bgBase,
                    textDecoration: 'none',
                    padding: '0 18px',
                    height: 36,
                    borderRadius: 7,
                    background: accent,
                    transition: 'opacity 0.15s',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.88')}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                >
                  Dashboard
                </Link>
              ) : (
                <>
                  <button
                    onClick={login}
                    style={{
                      
                      fontSize: 14,
                      fontWeight: 500,
                      color: colors.textSecondary,
                      padding: '0 14px',
                      height: 36,
                      borderRadius: 7,
                      background: 'transparent',
                      cursor: 'pointer',
                      transition: 'color 0.15s',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = colors.textPrimary)}
                    onMouseLeave={(e) => (e.currentTarget.style.color = colors.textSecondary)}
                  >
                    Sign in
                  </button>
                  <button
                    onClick={login}
                    style={{
                      
                      fontSize: 14,
                      fontWeight: 600,
                      color: colors.bgBase,
                      padding: '0 18px',
                      height: 36,
                      borderRadius: 7,
                      background: accent,
                      cursor: 'pointer',
                      transition: 'opacity 0.15s',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.88')}
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                  >
                    Get started
                  </button>
                </>
              )}
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen((v) => !v)}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              style={{
                display: isMobile ? 'flex' : 'none',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                borderRadius: 7,
                background: mobileOpen ? colors.bgSubtle : 'transparent',
                border: `1px solid ${mobileOpen ? colors.borderDefault : 'transparent'}`,
                cursor: 'pointer',
                color: colors.textPrimary,
                transition: 'background 0.15s, border-color 0.15s',
                flexShrink: 0,
                marginLeft: 'auto',
              }}
            >
              {mobileOpen ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <line x1="3" y1="8" x2="21" y2="8" /><line x1="3" y1="16" x2="21" y2="16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <MobileMenu
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        onLogin={login}
        isAuthenticated={isAuthenticated}
      />

      {/* Auth error toast */}
      <AnimatePresence>
        {authError && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            style={{
              position: 'fixed',
              top: 76,
              right: 24,
              zIndex: 200,
              background: colors.bgBase,
              border: `1px solid ${colors.borderDefault}`,
              borderRadius: 8,
              padding: '10px 16px',
              fontSize: 13,
              color: colors.textSecondary,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            }}
          >
            {authError}
            <button
              onClick={() => setAuthError(null)}
              style={{ marginLeft: 12, color: accent, cursor: 'pointer', fontWeight: 500, background: 'none', border: 'none' }}
            >
              Dismiss
            </button>
          </motion.div>
        )}
      </AnimatePresence>

    </>
  )
}
